/**
 * V2PageIndicatorElement - V2 wrapper for PageIndicatorElement
 *
 * Maps V2 PageIndicatorKind values (linearBar/segmented/segmentedTitle)
 * to V1 values (Line/Dashes/DashesWithTitle) used by the shared UI component.
 *
 * V1 uses defaults; V2 needs separate config per project convention.
 */

import React, { useCallback } from 'react';
import { PageIndicatorElement, type PageIndicatorElementProps, type PageIndicatorKindType } from './PageIndicatorElement';

/** V2 Page Indicator Kind values (from schema v2.15.0) */
export type V2PageIndicatorKindType = 'linearBar' | 'segmented' | 'segmentedTitle';

// ── V2 ↔ V1 mapping ──
const V2_TO_V1: Record<V2PageIndicatorKindType, PageIndicatorKindType> = {
  linearBar: 'Line',
  segmented: 'Dashes',
  segmentedTitle: 'DashesWithTitle',
};

const V1_TO_V2: Record<PageIndicatorKindType, V2PageIndicatorKindType> = {
  Line: 'linearBar',
  Dashes: 'segmented',
  DashesWithTitle: 'segmentedTitle',
};

/** Convert V2 kind to V1 kind for the shared component */
export function v2KindToV1(kind: V2PageIndicatorKindType): PageIndicatorKindType {
  return V2_TO_V1[kind] ?? 'Line';
}

/** Convert V1 kind to V2 kind for the V2 editor */
export function v1KindToV2(kind: PageIndicatorKindType): V2PageIndicatorKindType {
  return V1_TO_V2[kind] ?? 'linearBar';
}

/** Props for V2PageIndicatorElement - same as base but with V2 kind type */
export interface V2PageIndicatorElementProps extends Omit<PageIndicatorElementProps, 'indicatorKind' | 'onKindChange'> {
  indicatorKind: V2PageIndicatorKindType;
  onKindChange: (kind: V2PageIndicatorKindType) => void;
}

/**
 * V2PageIndicatorElement - wraps shared PageIndicatorElement with V2 kind mapping
 */
export const V2PageIndicatorElement: React.FC<V2PageIndicatorElementProps> = ({
  indicatorKind,
  onKindChange,
  ...rest
}) => {
  const handleKindChange = useCallback(
    (v1Kind: PageIndicatorKindType) => {
      onKindChange(v1KindToV2(v1Kind));
    },
    [onKindChange]
  );

  return (
    <PageIndicatorElement
      {...rest}
      indicatorKind={v2KindToV1(indicatorKind)}
      onKindChange={handleKindChange}
    />
  );
};

export default V2PageIndicatorElement;
